import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import ProgressBar from "@cloudscape-design/components/progress-bar";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { webhookBroadcastStatsModalStore } from "../stores/webhookBroadcastStatsModal";

function WebhookBroadcastStatsModal({ Interface }) {
	const modal = useRecoilValue(webhookBroadcastStatsModalStore);
	const setModal = useSetRecoilState(webhookBroadcastStatsModalStore);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState("");
	const [statistics, setStatistics] = React.useState(null);
	const [currentPage, setCurrentPage] = React.useState(1);
	const pageSize = 25;

	const loadStats = React.useCallback(
		async (page = 1) => {
			if (!modal.data?.id) return;

			setLoading(true);
			setError("");

			try {
				const response = await Interface.request(
					"get",
					{ class: "data", module: "webhookBroadcast", method: "statistics" },
					{
						broadcastId: modal.data.id,
						page,
						limit: pageSize,
					}
				);

				setStatistics({
					overall: response?.overall || {},
					users: response?.users?.data || [],
					usersPagination: {
						page: response?.users?.page || response?.users?.currentPage || page,
						pages: response?.users?.pages || response?.users?.totalPages || 0,
						total: response?.users?.total || response?.users?.totalUsers || 0,
					},
				});
				setCurrentPage(page);
			} catch (requestError) {
				setError(requestError?.text || requestError?.toString?.() || "Failed to load webhook broadcast statistics");
			} finally {
				setLoading(false);
			}
		},
		[Interface, modal.data, pageSize]
	);

	React.useEffect(() => {
		if (modal.show) {
			loadStats(1);
		}
	}, [modal.show, loadStats]);

	const closeModal = () => {
		setModal({
			...modal,
			show: false,
			data: null,
		});
		setStatistics(null);
		setError("");
		setCurrentPage(1);
	};

	const getStatusType = (status) => {
		if (status === "success") return "success";
		if (status === "failed" || status === "error") return "error";
		if (status === "pending") return "pending";

		return "info";
	};

	const formatDateTime = (value) => {
		if (!value) return "-";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;

		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	};

	const overall = statistics?.overall || modal.data || {};
	const totalUsers = Number(overall.totalUsers || 0);
	const sentCount = Number(overall.sentCount || 0);
	const progress = totalUsers ? Math.min(100, Math.round((sentCount / totalUsers) * 100)) : 0;

	return (
		<Modal
			visible={modal.show}
			onDismiss={closeModal}
			size="medium"
			header={`Webhook broadcast stats${modal.data?.name ? `: ${modal.data.name}` : ""}`}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button onClick={() => loadStats(currentPage)} loading={loading}>
							Refresh
						</Button>
						<Button variant="primary" onClick={closeModal}>
							Close
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			{loading && !statistics ? (
				<Box textAlign="center" padding="l">
					<Spinner size="large" />
				</Box>
			) : (
				<SpaceBetween size="m">
					{error ? <Alert type="error">{error}</Alert> : null}
					<Container header={<Header variant="h2">Statistics</Header>}>
						<SpaceBetween size="m">
							<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
								<Box>
									<Box variant="small">Users</Box>
									<Box variant="h2">{totalUsers}</Box>
								</Box>
								<Box>
									<Box variant="small">Sent</Box>
									<Box variant="h2">{sentCount}</Box>
								</Box>
								<Box>
									<Box variant="small">Success</Box>
									<Box variant="h2">{overall.successCount || 0}</Box>
								</Box>
								<Box>
									<Box variant="small">Failed</Box>
									<Box variant="h2">{overall.failedCount || 0}</Box>
								</Box>
								<Box>
									<Box variant="small">Pending</Box>
									<Box variant="h2">{overall.pendingCount || 0}</Box>
								</Box>
								<Box>
									<Box variant="small">Status</Box>
									<br/>
									<StatusIndicator type={getStatusType(modal.data?.status)}>{modal.data?.status || "-"}</StatusIndicator>
								</Box>
							</Grid>
							<ProgressBar value={progress} description={`${progress}% sent`} />
						</SpaceBetween>
					</Container>
				</SpaceBetween>
			)}
		</Modal>
	);
}

export default WebhookBroadcastStatsModal;
